import React, { useEffect, useMemo, useState } from 'react';
import { guildImg } from '../../utils/guildImage';

/**
 * **점령전 한 판의 현황** — 세 길드를 그래프 넷으로 나란히 견준다: 공격 성공 · 방어 성공 · 사용 몬스터 · 점수.
 * 그래프마다 길드 한 줄씩(순위 순), 줄 = 왼쪽 길드 엠블럼 · 막대 · 오른쪽 %와 숫자. **막대 색은 길드 색**이라 네 그래프를
 * 가로질러 같은 길드를 따라 읽는다(사용자 2026-10-13). 아래는 우리 길드가 공격한 길드·공격받은 길드(판수 — 우리 전투 기록, 사용자 2026-10-16).
 *
 * 숫자는 게임 패킷에서 온다(서버 `GET /api/siege-battles/match-summary`, 모델 `SiegeMatchSummary`). [전투 현황]·상대 기록을
 * 연 적이 없으면 다른 길드의 승패와 길드끼리 쓴 몹 수가 아직 없다 — 그 줄은 "—". 아무것도 없으면 `fallback`(예전 배너 +
 * 승/패 상자)을 그린다.
 */
interface GuildLine {
    guildId: number; guildName: string; matchScore: number; matchRank: number;
    playMemberCount: number; attackCount: number; attackUnitCount: number;
}
interface Stat { guildId: number; attackWin: number; attackTotal: number; defenseWin: number; defenseTotal: number }
interface Summary {
    ownGuildId: number; guilds: GuildLine[]; maxAttackUnitCount: number;
    /** 길드끼리 쓴 몹 수(패킷) — 판수로 바뀌어 지금은 안 쓴다(guildBattles 로 대체) */
    guildUsed: Array<{ guildId: number; targetGuildId: number; count: number }>;
    /** 길드끼리 **판수**(우리 전투 기록에서 센다, 사용자 2026-10-16) — 흐름 그래프가 이걸 쓴다 */
    guildBattles?: Array<{ guildId: number; targetGuildId: number; count: number }>;
    battleStats: Stat[];
}
type G = GuildLine & { place: number; color: string };

const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
/** 길드 색 — 우리 길드는 사이트 보라, 상대 둘은 주황·청록(순위 순). 모든 그래프가 같은 색을 쓴다 */
const OWN_COLOR = '#667eea';
const OTHER_COLORS = ['#f59e0b', '#14b8a6'];

function Emblem({ name, size }: { name: string; size: number }) {
    return (
        <img
            src={guildImg(name)}
            alt={name}
            title={name}
            loading="lazy"
            style={{ width: size, height: size, borderRadius: size * 0.24, objectFit: 'cover', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
            onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
        />
    );
}

const card: React.CSSProperties = {
    background: '#fff', borderRadius: 12, border: '1px solid rgba(0,0,0,0.07)', minWidth: 0, textAlign: 'left'
};

/**
 * 그래프 안의 한 줄 — 엠블럼 · 막대(길드 색) · 오른쪽에 큰 글자(`main`)와 작은 글자(`sub`).
 * `value` 는 막대 길이(0~1), 모르면 `null` → 막대 없이 "—".
 */
/**
 * 한 줄 — 엠블럼 · 막대. **% 와 횟수는 막대 안에** 쓴다(사용자) — 오른쪽 숫자 칸을 없애 막대가 카드 끝까지 간다.
 * 글자는 막대 가운데에 바탕 없이 쓰고, 채운 부분 위는 흰색·빈 부분 위는 진한 색.
 * 좁은 화면도 PC 와 같은 3열을 그대로 줄인다(모바일이 화면을 꽉 채웠다, 사용자).
 */
function Row({ g, value, main, sub, compact }: { g: G; value: number | null; main: string; sub?: string; compact?: boolean }) {
    const h = compact ? 14 : 20;
    const fill = value === null ? 0 : Math.round(Math.max(0, Math.min(1, value)) * 100);
    /**
     * 글자를 두 겹으로 그린다 — 아래는 진한 글자, 위는 흰 글자를 **채운 막대만큼만** 보이게 잘라(clip-path) 겹친다.
     * 가운데 글자가 길드 색 위에 걸치면 흰색, 빈 바탕 위면 진한 색이 되어 어느 쪽이든 읽힌다(사용자: 글자색을 알맞게).
     */
    const label = (dark: boolean) => (
        <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            ...(dark ? {} : { clipPath: `inset(0 ${100 - fill}% 0 0)` })
        }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: compact ? 2 : 4, whiteSpace: 'nowrap', padding: compact ? '0 3px' : '0 6px', lineHeight: compact ? '10px' : '14px' }}>
                <span style={{ fontSize: compact ? 9 : 12, fontWeight: 900, color: value === null ? '#9ca3af' : dark ? '#1f2937' : '#fff' }}>{value === null ? '—' : main}</span>
                {sub && value !== null && <span style={{ fontSize: compact ? 7 : 10, fontWeight: 700, color: dark ? '#4b5563' : 'rgba(255,255,255,0.9)' }}>{sub}</span>}
            </div>
        </div>
    );
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 4 : 8, minHeight: compact ? 20 : 30 }}>
            <Emblem name={g.guildName} size={compact ? 14 : 24} />
            <div style={{ position: 'relative', flex: 1, minWidth: 0, height: h, borderRadius: h / 2, background: 'rgba(255,255,255,0.9)', overflow: 'hidden' }}>
                {value !== null && <div style={{ width: `${fill}%`, height: '100%', borderRadius: h / 2, background: g.color, transition: 'width .4s ease' }} />}
                {label(true)}
                {value !== null && label(false)}
            </div>
        </div>
    );
}

/**
 * 카드 모양은 **사이트 카드와 같게** — 그룹 선택 카드(`GroupSelect`)·카운터 목록의 그라데이션 테두리(#667eea → #764ba2 → #f093fb)에
 * 흰색 → 연보라 바탕, 제목은 사이트 제목과 같은 그라데이션 글자. 막대가 길드 색이라 카드는 종류마다 색을 바꾸지 않는다
 * (종류마다 다른 색을 칠했더니 사이트와 안 맞았다, 사용자).
 */
function Chart({ title, children, compact }: { title: string; children: React.ReactNode; compact?: boolean }) {
    return (
        <div style={{
            ...card,
            border: '1.5px solid transparent',
            // 바탕은 사이트 매치업 배너와 같은 연보라(#eef2ff → #f5f3ff) — 흰색만으론 밋밋했다
            backgroundImage: 'linear-gradient(145deg, #eef2ff 0%, #f5f3ff 100%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            boxShadow: '0 2px 10px rgba(102, 126, 234, 0.10)',
            padding: compact ? '5px 6px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: compact ? 1 : 6
        }}>
            <div style={{
                fontSize: compact ? 10 : 14, fontWeight: 800, marginBottom: compact ? 1 : 2, whiteSpace: 'nowrap', alignSelf: 'flex-start',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text',
                WebkitTextFillColor: 'transparent', color: '#667eea'
            }}>{title}</div>
            {children}
        </div>
    );
}

export default function MatchSummary({ siegeId, fallback, compact }: { siegeId?: number; fallback: React.ReactNode; compact?: boolean }) {
    const [data, setData] = useState<Summary | null>(null);

    useEffect(() => {
        setData(null);
        if (!siegeId) return;
        let gone = false;
        fetch(`/api/siege-battles/match-summary?siegeId=${siegeId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => { if (!gone && j?.success) setData(j); })
            .catch(() => {});
        return () => { gone = true; };
    }, [siegeId]);

    // 순위대로 — 순위는 점수로 센다. 패킷의 match_rank 는 세 길드가 다 1 로 와서 판 안의 순서가 아니다
    const guilds: G[] = useMemo(() => {
        if (!data) return [];
        let other = 0;
        return [...data.guilds].sort((a, b) => b.matchScore - a.matchScore).map((g, i) => ({
            ...g, place: i + 1,
            color: g.guildId === data.ownGuildId ? OWN_COLOR : OTHER_COLORS[other++ % OTHER_COLORS.length]
        }));
    }, [data]);

    if (!data || !guilds.length) return <>{fallback}</>;

    const max = data.maxAttackUnitCount || 30;
    const statOf = (id: number) => data.battleStats.find((s) => s.guildId === id);
    const topScore = Math.max(1, ...guilds.map((g) => g.matchScore));
    /**
     * 방어 성공 막대는 **세 길드 중 가장 높은 방어율을 꽉 찬 것으로** 본다(사용자) — 방어 성공률은 10% 안팎이라 100% 기준이면
     * 막대가 거의 안 보였고, 30% 고정 기준보다 길드 사이 차이가 잘 보인다.
     */
    const defenseTop = Math.max(0.0001, ...data.battleStats.map((x) => x.defenseWin / Math.max(1, x.defenseTotal)));
    const own = data.ownGuildId;
    const byId = (id: number) => guilds.find((g) => g.guildId === id);
    // 흐름 숫자는 **판수**(우리 전투 기록) — 없으면(기록 전) 패킷의 마리 수로 물러난다
    const flowData = data.guildBattles && data.guildBattles.length ? data.guildBattles : data.guildUsed;
    const sent = flowData.filter((u) => u.guildId === own);
    const got = flowData.filter((u) => u.targetGuildId === own);
    const flowMax = Math.max(1, ...sent.map((u) => u.count), ...got.map((u) => u.count));

    return (
        <div style={{
            // 카드를 감싸는 틀은 두지 않는다(사용자) — 카드만 놓인다
            marginBottom: compact ? 16 : 24, maxWidth: 980, marginLeft: 'auto', marginRight: 'auto', textAlign: 'left'
        }}>
            {/* 위 줄: 공격 성공 · 방어 성공 · 사용 몬스터 — 좁은 화면도 같은 3열(작게) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: compact ? 4 : 10 }}>
                <Chart title="공격 성공" compact={compact}>
                    {guilds.map((g) => {
                        const s = statOf(g.guildId);
                        return <Row key={g.guildId} g={g} compact={compact} value={s ? s.attackWin / Math.max(1, s.attackTotal) : null}
                            main={s ? `${pct(s.attackWin, s.attackTotal)}%` : ''} sub={s ? `${s.attackWin}/${s.attackTotal}` : ''} />;
                    })}
                </Chart>
                <Chart title="방어 성공" compact={compact}>
                    {guilds.map((g) => {
                        const s = statOf(g.guildId);
                        return <Row key={g.guildId} g={g} compact={compact} value={s ? s.defenseWin / Math.max(1, s.defenseTotal) / defenseTop : null}
                            main={s ? `${pct(s.defenseWin, s.defenseTotal)}%` : ''} sub={s ? `${s.defenseWin}/${s.defenseTotal}` : ''} />;
                    })}
                </Chart>
                <Chart title="사용 몬스터" compact={compact}>
                    {guilds.map((g) => {
                        const cap = g.playMemberCount * max;
                        return <Row key={g.guildId} g={g} compact={compact} value={g.attackUnitCount / Math.max(1, cap)}
                            main={`${pct(g.attackUnitCount, cap)}%`} sub={`${g.attackUnitCount}/${cap}`} />;
                    })}
                </Chart>
            </div>

            {/* 아래 줄: 우리 길드가 공격한 길드 · 공격받은 길드(판수 — 우리 전투 기록) · 점수 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: compact ? 4 : 10, marginTop: compact ? 4 : 10 }}>
                {(sent.length > 0 || got.length > 0) && ([
                    ['공격한 길드', sent.map((u) => ({ id: u.targetGuildId, n: u.count }))],
                    ['공격받은 길드', got.map((u) => ({ id: u.guildId, n: u.count }))]
                ] as Array<[string, Array<{ id: number; n: number }>]>).map(([title, list]) => (
                    <Chart key={title} title={title} compact={compact}>
                        {list.map(({ id, n }) => {
                            const g = byId(id);
                            return g ? <Row key={id} g={g} compact={compact} value={n / flowMax} main={`${n}`} /> : null;
                        })}
                    </Chart>
                ))}
                <Chart title="점수" compact={compact}>
                    {guilds.map((g) => (
                        <Row key={g.guildId} g={g} compact={compact} value={g.matchScore / topScore}
                            main={Math.floor(g.matchScore).toLocaleString()} sub={`${g.place}위`} />
                    ))}
                </Chart>
            </div>
        </div>
    );
}
